import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./authConfig.jsx";
import { setMsalInstance, setLastLoginToken } from "./utils/msalInstance";

// Import global fetch interceptor (keep this)
import "./fetchInterceptor";

// --- Canonical auth-origin guard (client-side backstop) --------------------
// www.cogentplatform.com is the single canonical origin (marketing + app). The
// web.config redirects the apex and app. subdomain there at the server, but keep
// this JS backstop too: if login STARTS on a non-canonical origin, MSAL would
// store the PKCE verifier there while Entra returns the code to www (a different
// origin) → the code is dropped and the login screen re-renders → a second
// sign-in. Redirect apex/app. → www BEFORE MSAL initializes, so the verifier is
// stored and the code is redeemed on the SAME origin. Skip when a #code is
// already present (a return trip must never be re-routed). www itself is NOT in
// the list (it's the destination — including it would loop). Inert on dev and www.
if (typeof window !== "undefined") {
  const legacyHost =
    window.location.hostname === "cogentplatform.com" ||
    window.location.hostname === "app.cogentplatform.com";
  // Never re-route a /login return trip that still carries the auth #code — that
  // would re-split an in-flight redemption. Otherwise redirect EVERY legacy-origin
  // path → www (all paths, not just /login). The frontend App Service is Linux, so
  // web.config/IIS rewrites don't apply — this client-side redirect (before MSAL
  // init) IS the canonical-origin mechanism. Path + query + hash preserved so deep
  // links / ?email invites survive. (A server-side 301 for SEO would need a custom
  // startup server; deferred — this fixes the functional origin split.)
  const isLoginReturn =
    window.location.pathname === "/login" && window.location.hash.includes("code=");
  if (legacyHost && !isLoginReturn) {
    window.location.replace(
      "https://www.cogentplatform.com" +
        window.location.pathname +
        window.location.search +
        window.location.hash
    );
  }
}

// Initialize MSAL instance for Azure Entra ID (External ID)
// MSAL v4 requires explicit initialize() before any operations (handleRedirectPromise, etc.)
const msalInstance = new PublicClientApplication(msalConfig);

// Expose the instance to non-React modules (the fetch interceptor uses it to
// silently refresh an expired access token on a 401, avoiding a second sign-in).
setMsalInstance(msalInstance);

// Capture the access token the login itself issues, and mark the just-signed-in
// account active. This lets completeAuth use the login's own token instead of
// re-acquiring it via acquireTokenSilent — which, if it fails right after login,
// was forcing a second interactive sign-in. Registered before render so the
// LOGIN_SUCCESS fired during handleRedirectPromise is caught.
msalInstance.addEventCallback((event) => {
  const payload = event && event.payload;
  if (!payload) return;
  if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
    if (payload.account) msalInstance.setActiveAccount(payload.account);
    if (payload.accessToken) setLastLoginToken(payload.accessToken);
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));

const renderApp = () =>
  root.render(
    <React.StrictMode>
      <Suspense fallback="Loading...">
        <HelmetProvider>
          <BrowserRouter>
            <MsalProvider instance={msalInstance}>
              <App />
            </MsalProvider>
          </BrowserRouter>
        </HelmetProvider>
      </Suspense>
    </React.StrictMode>
  );

// Initialize MSAL, then redeem any pending redirect BEFORE the first render.
// This is the canonical MSAL startup order: no component (e.g. HomeRedirect) can
// act on auth state while MSAL is still redeeming the authorization code.
// Relying on MsalProvider's background redemption left a timing window in which a
// mid-flight login could be bounced back to /login — the intermittent double
// sign-in. Awaiting handleRedirectPromise here closes that race at the source
// (the inProgress gates in the app are the belt to this suspenders). The
// LOGIN_SUCCESS event callback above still captures the token.
msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((result) => {
    const account =
      (result && result.account) ||
      msalInstance.getActiveAccount() ||
      msalInstance.getAllAccounts()[0];
    if (account) msalInstance.setActiveAccount(account);
  })
  .catch((e) => {
    // A redemption error must never block render — the login page and
    // completeAuth recover. Log it for the auth investigation.
    console.warn("[auth][startup] handleRedirectPromise failed:", e && (e.errorCode || e.message));
  })
  .finally(renderApp);

// Optional: performance monitoring
reportWebVitals();
